import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import Swal from "sweetalert2";
import { Link, useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";

import { URL_REGEX } from "../../utils/Constants";
import hasPermission from "../../utils/hasMultiplePermission";
import { GetListOfBrandSettingCancelToken } from "../../services/BrandServices";
import {
  addAndUpdateReportCancelToken,
  getReportCategories,
  getReportDetails,
  previewReportCancelToken,
  getCmsUsersDropdown
} from "../../services/reportService";
import { REPORT_STATUS_OPTIONS } from "./ReportHelpers";
import { usePermissionGuard } from "../../hooks/usePermissionGuard";
import { GetUserRolesCancelToken } from "../../services/UserService";

const GLOBAL_BRAND_OPTION = { value: 0, label: "Global" };

/**
 * Create and edit form for Power BI reports with inline embed preview.
 * @returns {JSX.Element} Report create/edit form.
 */
const ReportForm = () => {
  const history = useHistory();
  const { tab, id } = useParams();
  const givenPermissions = useSelector((state) => state.givenPermission);
  const loggedInUserId = useSelector((state) => state.userData?.id);
  const previewCancelTokenRef = useRef(null);

  const canViewGlobalReports = hasPermission({
    scopes: ["reportsnonassignedview"],
    permissions: givenPermissions,
  });
  const canEditReport = hasPermission({
    scopes: ["reportsedit"],
    permissions: givenPermissions,
  });
  const canAddReport = hasPermission({
    scopes: ["reportsadd"],
    permissions: givenPermissions,
  });

  const isEdit = tab === "open" && !!id;

  const [reportData, setReportData] = useState({});
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [powerBIConfig, setPowerBIConfig] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [brandDropdownData, setBrandDropdownData] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);


  const isSaveDisabled = disabled || (isEdit ? !canEditReport : !canAddReport);
  
  const brandOptions = useMemo(() => {
    if (canViewGlobalReports) {
      return brandDropdownData;
    }
    return brandDropdownData.filter((brand) => Number(brand.value) !== 0);
  }, [brandDropdownData, canViewGlobalReports]);

  usePermissionGuard({
    scopes: ["reportsadd"],
    onNoAccess: () => {
      if (!isEdit) {
        history.push("/noaccess");
      }
    },
    dependencies: [id],
  });

  const handlePreviewReport = useCallback(async (reportUrl) => {
    if (!reportUrl || !reportUrl.match(URL_REGEX)) {
      await Swal.fire({
        icon: "warning",
        title: "Invalid URL",
        text: "Please enter a valid Report URL to preview.",
      });
      return;
    }

    if (previewCancelTokenRef.current) {
      previewCancelTokenRef.current.cancel("Starting a new preview request");
    }

    const cancelTokenSource = axios.CancelToken.source();
    previewCancelTokenRef.current = cancelTokenSource;

    setPreviewLoading(true);
    setPowerBIConfig(null);

    try {
      const res = await previewReportCancelToken(
        { url: reportUrl },
        cancelTokenSource.token
      );

      if (res.status === 200 || res.data?.status) {
        const data = res.data?.data || res.data;
        setPowerBIConfig({
          type: "report",
          id: data.reportId,
          embedUrl: data.embedUrl,
          accessToken: data.embedToken,
          tokenType: models.TokenType.Embed,
          settings: {
            panes: {
              filters: {
                expanded: false,
                visible: false,
              },
            },
            navContentPaneEnabled: true,
          },
        });
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        await Swal.fire({
          icon: "error",
          title: "Preview Error",
          text: "Could not load the Power BI report. Please check the URL.",
          timer : 2000
        });
      }
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();

    const loadDropdowns = async () => {
      try {
        const brandRes = await GetListOfBrandSettingCancelToken(cancelTokenSource.token);
        setBrandDropdownData([
          GLOBAL_BRAND_OPTION,
          ...(brandRes.data?.brand_setting_list || []).map((brand) => ({
            value: brand.id,
            label: brand.brand_name,
          })),
        ]);

        const categoryRes = await getReportCategories(undefined, cancelTokenSource.token);
        setCategoryOptions(
          (categoryRes.data?.reportCategories || []).map((category) => ({
            value: category.value,
            label: category.label,
          }))
        );

        const userRes = await getCmsUsersDropdown(undefined, cancelTokenSource.token);
        setUserOptions(
          (userRes.data || []).map((user) => ({
            value: user.value,
            label: user.label,
          }))
        );

        const roleRes = await GetUserRolesCancelToken(undefined, cancelTokenSource.token);
        setRoleOptions(
          (roleRes.data || []).map((role) => ({
            value: role.role_id,
            label: role.role_name,
          }))
        );
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error(error);
        }
      }
    };

    loadDropdowns();

    return () => {
      cancelTokenSource.cancel("ReportForm dropdown load cancelled");
    };
  }, []);

  useEffect(() => {
    if (!isEdit) {
      setReportData({});
      setPowerBIConfig(null);
      return undefined;
    }

    const cancelTokenSource = axios.CancelToken.source();

    const fetchReport = async () => {
      try {
        const res = await getReportDetails({ id }, cancelTokenSource.token);

        if (res.data?.status) {
          const report = res.data.data;
          setReportData(report);

          if (report.report_url) {
            handlePreviewReport(report.report_url);
          }
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          await Swal.fire({
            icon: "info",
            title: "Info",
            text: "No record found.",
          });
          history.push("/reports/table");
        }
      }
    };

    fetchReport();

    return () => {
      cancelTokenSource.cancel("ReportForm edit load cancelled");
    };
  }, [id, isEdit, history, handlePreviewReport]);

  useEffect(
    () => () => {
      if (previewCancelTokenRef.current) {
        previewCancelTokenRef.current.cancel("ReportForm unmounted");
      }
    },
    []
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: reportData?.name || "",
      category_id: reportData?.category_id || "",
      status: reportData?.status ?? "",
      brand_id: reportData?.brand_id ?? reportData?.brand ?? "",
      report_url: reportData?.report_url || "",
      publish_report: reportData?.publish_report || 0,
      users: Array.isArray(reportData?.users) ? reportData.users : [],
      roles: Array.isArray(reportData?.roles) ? reportData.roles : [],
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().required("Name is required"),
      status: Yup.string().required("Status is required"),
      brand_id: Yup.mixed()
        .test(
          "required",
          "Brand is required",
          (value) => value !== "" && value !== null && value !== undefined
        )
        .required("Brand is required"),
      report_url: Yup.string()
        .trim()
        .required("Report URL is required")
        .url("Please enter valid URL")
        .matches(URL_REGEX, "URL is not valid"),
    }),
    onSubmit: async (values) => {
      setDisabled(true);

      try {
        const payload = { ...values };
        if (tab === "open") {
          payload.id = id;
        }

        const response = await addAndUpdateReportCancelToken(payload);

        if (response.data?.status) {
          await Swal.fire({
            icon: "success",
            title: "Success",
            text: isEdit
              ? "Report updated successfully"
              : "Report created successfully",
            timer: 2000,
          });

          if (!isEdit) {
            history.push("/reports/table");
          }
        }
      } catch (error) {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || "Something went wrong",
        });
      } finally {
        setDisabled(false);
      }
    },
  });

  /** Selected users for the multi-select. */
  const selectedUserOptions = useMemo(
    () => userOptions.length > 0 && userOptions.filter((option) => formik.values.users.includes(option.value)),
    [userOptions, formik.values.users]
  );

  /** Selected roles for the multi-select. */
  const selectedRoleOptions = useMemo(
    () => roleOptions.filter((option) => formik.values.roles.includes(option.value)),
    [roleOptions, formik.values.roles]
  );

  /** Updates selected users. */
  const handleUserChange = useCallback(
    (selected) => {
      formik.setFieldValue("users", selected ? selected.map((option) => option.value) : []);
    },
    [formik]
  );

  /** Updates selected roles. */
  const handleRoleChange = useCallback(
    (selected) => {
      formik.setFieldValue("roles", selected ? selected.map((option) => option.value) : []);
    },
    [formik]
  );

  return (
    <div className="row">
      <div className="col-12 col-md-6 col-lg-8">
        <div className="card card-body-inr">
          <form onSubmit={formik.handleSubmit}>
            <div className="row">
              <div className="col-md-12">
                <div className="edit-icon new-card-header">
                  <h4 className="card-header">Report Information</h4>
                </div>
              </div>

              <div className="col-md-6 col-lg-6">
                <label htmlFor="name">Report Name *</label>
                <div className="form-icon-group mb-4" title="Report Name">
                  <input
                    type="text"
                    id="name"
                    className={
                      "form-control " +
                      (formik.errors.name && formik.touched.name ? "is-invalid" : "")
                    }
                    name="name"
                    placeholder="Enter report name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
              </div>

              <div className="col-md-6 col-lg-6">
                <label>Brand *</label>
                <div className="form-icon-group mb-4" title="Brand">
                  <Select
                    className={`form-control custom-select-box ${
                      formik.touched.brand_id && formik.errors.brand_id && "is-invalid"
                    }`}
                    name="brand_id"
                    value={
                      brandOptions.find(
                        (item) => String(item.value) === String(formik.values.brand_id)
                      ) || null
                    }
                    onChange={(option) => {
                      formik.setFieldValue("brand_id", option ? option.value : "");
                    }}
                    onBlur={formik.handleBlur}
                    options={brandOptions}
                    placeholder="Select"
                    isClearable
                    maxMenuHeight={175}
                  />
                </div>
              </div>

              <div className="col-md-6 col-lg-6">
                <label>
                  Category <i className="fal fa-info-circle"></i>
                </label>
                <div className="form-icon-group mb-4" title="Category">
                  <Select
                    className={`form-control custom-select-box ${formik.touched.category_id && formik.errors.category_id && "is-invalid"}`}
                    value={categoryOptions.find(
                      (option) => option.value === formik.values.category_id
                    )}
                    onChange={(value) => {
                      formik.setFieldValue("category_id", value?.value || "");
                    }}
                    options={categoryOptions}
                    placeholder="Select"
                    isClearable
                  />
                </div>
              </div>

              <div className="col-md-6 col-lg-6">
                <label>Status *</label>
                <div className="form-icon-group mb-4" title="Status">
                  <Select
                    className={`form-control custom-select-box ${formik.touched.status && formik.errors.status && "is-invalid"}`}
                    value={
                      REPORT_STATUS_OPTIONS.find(
                        (item) => item.value === formik.values.status
                      ) || null
                    }
                    onChange={(value) => {
                      formik.setFieldValue("status", value?.value ?? "");
                    }}
                    options={REPORT_STATUS_OPTIONS}
                    placeholder="Select"
                    isClearable
                  />
                </div>
              </div>

              <div className="col-md-6">
                <label>Users</label>
                <div className="form-icon-group mb-4" title="Reports To">
                  <Select
                    className="form-control custom-select-box"
                    name="users"
                    isMulti
                    value={selectedUserOptions}
                    onChange={handleUserChange}
                    onBlur={formik.handleBlur}
                    options={userOptions}
                    maxMenuHeight={175}
                    placeholder="Select"
                    isClearable
                  />
                  {formik.errors.users && formik.touched.users ? (
                    <span className="exclamation">
                      <i className="fal fa-exclamation-circle"></i>
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="col-md-6">
                <label>Roles</label>
                <div className="form-icon-group mb-4" title="Role">
                  <Select
                    className="form-control custom-select-box"
                    name="roles"
                    isMulti
                    value={selectedRoleOptions}
                    onChange={handleRoleChange}
                    onBlur={formik.handleBlur}
                    options={roleOptions}
                    maxMenuHeight={175}
                    placeholder="Select"
                    isClearable
                  />
                  {formik.errors.roles && formik.touched.roles ? (
                    <span className="exclamation">
                      <i className="fal fa-exclamation-circle"></i>
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="col-md-12 col-lg-12">
                <label>PowerBI Report URL *</label>
                <div className="form-icon-group" title="PowerBI Report URL">
                  <input
                    type="text"
                    className={
                      "form-control " +
                      (formik.errors.report_url && formik.touched.report_url ? "is-invalid" : "")
                    }
                    name="report_url"
                    placeholder="https://app.powerbi.com/..."
                    value={formik.values.report_url}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                {formik.values.report_url.trim() !== "" && (
                  <div className="d-flex justify-content-end mt-2">
                    <a
                      href={formik.values.report_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fa fa-external-link" /> Open in Power BI
                    </a>
                  </div>
                )}
              </div>

              <div className="col-md-12">
                <div className="edit-icon new-card-header mt-4">
                  <h4 className="card-header heading_color_dynamic">
                    <span>
                      <i className="fal fa-cog"></i>
                    </span>{" "}
                    Report Settings
                  </h4>
                </div>
              </div>

              <div className="col-md-6 col-lg-3 d-flex align-items-center mt-2">
                <div className="d-flex" title="Public Report">
                  <label className="mb-0 mr-1">
                    Public Report{" "}
                    <i title="Public Report" className="fal fa-info-circle grade-icon"></i>
                  </label>
                  <div className="toggle-switch" title="Public Report">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={formik.values.publish_report === 1}
                        onChange={(e) => {
                          formik.setFieldValue("publish_report", e.target.checked ? 1 : 0);
                        }}
                      />
                      <span className="slider slider-round"></span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-group form-group-save-cancel mt-4 col-12">
                <button
                  className="btn btn-save btn-success"
                  type="submit"
                  disabled={isSaveDisabled}
                  title="Save Report"
                >
                  {disabled ? (
                    <i className="fas fa-cog fa-spin"></i>
                  ) : (
                    <i className="fal fa-save"></i>
                  )}
                  Save
                </button>

                {tab !== "create" && (
                  <Link
                    className="btn btn-white-bordered"
                    type="button"
                    title="Preview Report"
                    to={{
                      pathname: `/reports/preview/${id}`,
                      state: { reportName: reportData?.name || formik.values.name },
                    }}
                  >
                    <i className="fa fa-eye"></i>
                    Preview
                  </Link>
                )}

                <button
                  title="Cancel"
                  type="button"
                  className="btn btn-close btn-danger"
                  disabled={disabled}
                  onClick={() => {
                    tab === "create" ? history.push("/reports/table") : formik.resetForm();
                  }}
                >
                  <i className="fal fa-times"></i>
                  Cancel
                </button>
              </div>

              {Object.keys(formik.errors).map((key) => {
                if (formik.touched[key]) {
                  return (
                    <div key={key} className="invalid-feedback d-block ml-3">
                      {formik.errors[key]}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </form>
        </div>
      </div>
      <div className="col-12 col-md-6 col-lg-4">
        <div className="card card-body-inr">
          <div className="row">
            <div className="col-md-12">
              <div className="edit-icon new-card-header">
                <h4 className="card-header">Preview</h4>
              </div>
            </div>
            <div className="col-md-12">
              <div className="powerbi-preview-wrapper">
                {previewLoading ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                ) : powerBIConfig ? (
                  <PowerBIEmbed embedConfig={powerBIConfig} cssClassName="powerbi-container" />
                ) : (
                  <div className="d-flex justify-content-center align-items-center text-muted p-4 text-center">
                    <div>
                      <i className="fal fa-chart-bar fa-3x mb-3"></i>
                      <h6>Report Preview</h6>
                      <p className="small mb-0">
                        Enter a valid URL to view the report preview.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportForm;
